/**
 * Fonte ÚNICA do rótulo de papel exibido ao usuário (E52.F3 / F-E52.F1-05).
 *
 * A tela de usuários já traduzia `certificador`, mas o perfil pessoal
 * comparava só `owner` e mandava todo o resto para o fallback "Usuário" — um
 * certificador via uma representação errada do próprio papel. Com duas cópias
 * da régua, corrigir uma não corrige a outra; por isso a decisão de rótulo
 * mora aqui e as telas apenas a consomem.
 */
import { ptBR } from '@/i18n/pt-BR';
import type { PerfilRole } from '@/lib/auth';

const LABELS: Record<PerfilRole, string> = {
  owner: ptBR.members.roleOwner,
  member: ptBR.members.roleMember,
  revisor: ptBR.members.roleRevisor,
  certificador: ptBR.members.roleCertificador,
  bank_manager: ptBR.members.roleBankManager,
  bank_user: ptBR.members.roleBankUser,
};

/** Rótulo do papel; papel desconhecido cai no rótulo genérico de usuário. */
export function roleLabel(role: PerfilRole | string | null | undefined): string {
  if (role === null || role === undefined) return ptBR.members.roleMember;
  return LABELS[role as PerfilRole] ?? ptBR.members.roleMember;
}

/**
 * Quem assina o laudo e pode ser o responsável técnico (E175.F1, ADR-0127 §4): o
 * certificador e o administrador, sempre no escritório-plataforma. Mesmo
 * conjunto que `PAPEIS_QUE_ASSINAM` no servidor.
 */
export function assinaLaudos(role: string | null | undefined, isPlatform: boolean): boolean {
  return isPlatform && (role === 'certificador' || role === 'owner');
}

/**
 * O valor é mesmo um papel? Quem lê dado dinâmico do servidor (a trilha de
 * auditoria) precisa saber ANTES de traduzir: `roleLabel` responde "Usuário"
 * para qualquer coisa, e um valor que não é papel viraria um papel plausível.
 */
export function isPapelConhecido(valor: unknown): valor is PerfilRole {
  return typeof valor === 'string' && valor in LABELS;
}
